import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, PolygonElementParams, PrimitiveElementDef } from '../../graphpad.model';
import type { CreatedElement, ElementContext, ElementCreator } from '../element.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../features/feature-style.model';
import { resolvePosition } from './position';

/**
 * Creates a polygon primitive from a list of vertex positions.
 *
 * Params define identity: vertex positions (fixed, expression, or relative).
 * Display defines styling: polygon fill, border, vertex points.
 */
export class PolygonCreator implements ElementCreator<PrimitiveElementDef> {
  readonly types = ['polygon'];

  create(
    id: string,
    def: PrimitiveElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { elementFactory, featureTheme } = context;
    const params = def.params as PolygonElementParams;
    const objects: GeometryElement[] = [];

    // Resolve each vertex position
    const vertexPoints: GeometryElement[] = [];
    for (let i = 0; i < params.vertices.length; i++) {
      const [getX, getY] = resolvePosition(params.vertices[i], context);
      const vertexName = params.vertexNames?.[i];

      const pointResult = elementFactory.point({
        coords: [getX, getY],
        name: vertexName,
        style: { visible: false, size: 0 },
      });
      objects.push(pointResult.point);
      vertexPoints.push(pointResult.point);
    }

    // Resolve polygon style
    const polygonStyle = resolveFeatureStyle('polygon', 'polygon', context, display, []);
    const visible = resolveFeatureVisibility('polygon', featureTheme, display, []);

    // Create polygon
    const polygonResult = elementFactory.polygon({
      vertices: vertexPoints,
      name: params.name ?? id,
      style: {
        ...polygonStyle,
        visible,
      },
    });
    objects.push(polygonResult.polygon);

    return {
      id,
      type: 'polygon',
      objects,
      destroy: () => objects.forEach((obj) => context.board.removeObject(obj)),
    };
  }
}
